import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getJson } from '@/lib/api';

/**
 * Settings → Features: switches for the newer parts of the app. A feature
 * that is off is hidden for everyone, admins included: its screens, BESTie's
 * tools for it, and its What's new notes. Its data stays, so switching it back
 * on picks up where it left off.
 *
 * The keys mirror `apps/api/src/lib/app-features.ts`, which also owns the
 * labels and descriptions Settings shows.
 */
export const APP_FEATURE = {
  assistantCategories: 'assistant-categories',
  materials: 'materials',
  messages: 'messages',
  proposals: 'proposals',
  pageLibrary: 'page-library',
  noteTemplates: 'note-templates',
  returnLabels: 'return-labels',
} as const;
export type AppFeatureKey = (typeof APP_FEATURE)[keyof typeof APP_FEATURE];

/** The system setting that stores the switches. */
export const APP_FEATURES_SETTING = 'app-features';

/** One switchable feature, as Settings lists it. */
export interface AppFeatureDefinition {
  readonly key: AppFeatureKey;
  readonly label: string;
  readonly description: string;
}

/** A partial answer per feature, as Settings stores it. */
export type AppFeatureFlags = Partial<Record<AppFeatureKey, boolean>>;

export interface AppFeaturesView {
  readonly catalog: readonly AppFeatureDefinition[];
  readonly enabled: AppFeatureFlags;
}

export const APP_FEATURES_QUERY_KEY = ['app-features'] as const;

export const readAppFeatures = (signal?: AbortSignal): Promise<AppFeaturesView> =>
  getJson<AppFeaturesView>('/app-features', undefined, signal);

/** Whether a feature is on. Anything not answered (still loading, unreadable) is on. */
export type IsFeatureOn = (key: AppFeatureKey) => boolean;

/**
 * Which app features are on. Treats everything as on until the answer
 * arrives, so a slow or failed read never hides a screen someone is using.
 *
 * @returns A lookup for one feature.
 */
export function useAppFeatures(): IsFeatureOn {
  const features = useQuery({
    queryKey: APP_FEATURES_QUERY_KEY,
    queryFn: ({ signal }) => readAppFeatures(signal),
    staleTime: 5 * 60_000,
  });
  const enabled = features.data?.enabled;
  // Stable per answer, so callers can list it as a memo dependency.
  return useCallback<IsFeatureOn>((key) => enabled?.[key] ?? true, [enabled]);
}

const APP_FEATURE_KEYS: readonly string[] = Object.values(APP_FEATURE);

export function isAppFeatureKey(value: string): value is AppFeatureKey {
  return APP_FEATURE_KEYS.includes(value);
}

/**
 * The same answer for a key written in free text, like a help page's
 * `feature:` line. A key that isn't a feature is on, so a typo never hides
 * a page.
 *
 * @returns A lookup for one feature key.
 */
export function useFeatureLookup(): (feature: string) => boolean {
  const isFeatureOn = useAppFeatures();
  return useCallback((feature: string) => !isAppFeatureKey(feature) || isFeatureOn(feature), [isFeatureOn]);
}
